import { Box, Button, Divider, Stack } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CONFIG } from "../../../config";
import useRazorpay from "react-razorpay";
import PaymentSuccess from "../../components/PaymentSuccess";
import PaymentFailed from "../../components/PaymentFailed";
import { Puff } from "react-loader-spinner";
import PaymentError from "../../components/PaymentError";
import { PaymentStatus, PaymentStatusPrefix } from "../../../constants";

const apiKey = CONFIG.RAZORPAY_KEY_ID;

const PaymentPage = () => {
  const navigate = useNavigate();
  const isPaymentProcessing = useRef(false);
  const [queryParameters] = useSearchParams();
  const orderId = queryParameters.get("orderId");
  const amount = queryParameters.get("amount");
  const contact = queryParameters.get("mobileNo");

  const [paymentStatus, setPaymentStatus] = useState(PaymentStatus.Unknown);
  const [paymentDetail, setPaymentDetail] = useState(null);

  const [Razorpay] = useRazorpay();

  const [loading, setLoading] = useState(false);

  const successPaymentCallback = (response) => {
    // Handle the payment success response
    const payDetail = {
      razorpay_payment_id: response.razorpay_payment_id,
      razorpay_order_id: response.razorpay_order_id ?? orderId,
      razorpay_signature: response.razorpay_signature,
      totalAmount: +amount,
      status: "Success",
    };
    setPaymentDetail(payDetail);
    setPaymentStatus(PaymentStatus.Success);
    handleSuccessPayment();
  };

  const failedPaymentCallback = (response) => {
    // Handle the payment failed response
    const payDetail = {
      razorpay_payment_id: response.error.metadata.payment_id,
      razorpay_order_id: response.error.metadata.order_id ?? orderId,
      totalAmount: +amount,
      status: "Failed",
    };
    setPaymentDetail(payDetail);
    setPaymentStatus(PaymentStatus.Failed);
    handleFailedPayment();
  };

  const handleSuccessPayment = () => {
    navigate(
      `/checkout?${PaymentStatusPrefix}${PaymentStatus.Success}&orderId=${paymentDetail?.razorpay_order_id}`,
      {
        replace: true,
      }
    );
  };

  const handleFailedPayment = () => {
    navigate(
      `/checkout?${PaymentStatusPrefix}${PaymentStatus.Failed}&orderId=${paymentDetail?.razorpay_order_id}`,
      {
        replace: true,
      }
    );
  };

  const handleClosePayment = () => {
    navigate(
      `/checkout?${PaymentStatusPrefix}${PaymentStatus.Close}&orderId=${paymentDetail?.razorpay_order_id}`,
      {
        replace: true,
      }
    );
  };

  const handleRetryPayment = () => {
    setPaymentStatus(PaymentStatus.Unknown);
    setPaymentDetail(null);
    handlePayNow(true);
  };

  const handlePayNow = async () => {
    if (!orderId || !amount) {
      setPaymentStatus(PaymentStatus.Error);
      setLoading(false);
      isPaymentProcessing.current = false;
      return;
    }
    isPaymentProcessing.current = true;
    setLoading(true);
    setPaymentStatus(PaymentStatus.Unknown);

    // Razorpay options
    const options = {
      key: apiKey,
      amount: +amount * 100,
      currency: "INR",
      name: "STARNEX",
      description: "Starnex Subscription Transaction",
      order_id: orderId,
      image:
        "https://play-lh.googleusercontent.com/py7T41gEU6QmDSIov6w8Wj6lDSlbrSx692WXh48YjS5fUV8SDubgaxLPmY0_TrKgD8s=w480-h960-rw",
      handler: successPaymentCallback,
      prefill: contact
        ? {
            contact: contact,
          }
        : undefined,
      readonly: {
        contact: true,
        email: true,
        name: true,
      },
      notes: {
        address: "Razorpay Corporate Office",
      },
      theme: {
        color: "#2F80ED",
      },
      modal: {
        ondismiss: handleClosePayment,
      },
    };
    setTimeout(() => {
      setLoading(false);
      const razor = new Razorpay(options);
      razor.open();
      razor.on("payment.failed", failedPaymentCallback);
    }, 2000);
  };

  useEffect(() => {
    if (!isPaymentProcessing.current) handlePayNow();
  }, []);

  return (
    <Box className="flex flex-col items-center gap-6 bg-white py-5">
      {loading ? (
        <Stack
          className="bg-[#FFFFFF] w-full h-[80vh] py-4 px-5"
          sx={{ padding: "15px" }}
          direction={"column"}
          alignItems={"center"}
          justifyContent={"center"}
          spacing={3}
        >
          <Puff visible height="80" width="80" color="#2F80ED" />
          <h1 className="text-lg font-[400] my-3 font-['Poppins-Medium'] text-center px-4 animate-pulse">
            Please wait while processing payment...
          </h1>
        </Stack>
      ) : (
        <div className="w-full max-w-[500px] gap-4 px-4">
          {paymentStatus === PaymentStatus.Success && (
            <PaymentSuccess handleContinue={handleClosePayment} />
          )}
          {paymentStatus === PaymentStatus.Failed && (
            <PaymentFailed
              handleContinue={handleClosePayment}
              handleTryAgain={handleRetryPayment}
            />
          )}
          {paymentStatus === PaymentStatus.Error && (
            <PaymentError
              handleContinue={handleClosePayment}
              handleTryAgain={handleRetryPayment}
            />
          )}
        </div>
      )}
    </Box>
  );
};

export default PaymentPage;
